// L4 (boot-health #3986): decide whether a couch-login retry should run.
//
// The splash "Retry" button calls retryCouchLoginAsync(manual=true). That method used
// to bail at three gates before it could do anything:
//   1. couchLoginRetryNeeded === false  — but a PRE-DEFERRAL hang (e.g. the boot wedged
//      in the reachability probe, L3, before couchLoginRetryNeeded was ever armed) leaves
//      that flag false, so Retry did nothing.
//   2. !navigator.onLine                — an offline retry genuinely is a no-op.
//   3. !this.user                        — in a pre-login hang the user has not been
//      emitted yet, so Retry bailed instead of trying to (re)establish the session.
// The net effect: "Retry does nothing" on a stuck splash.
//
// This pure decision function encodes the fix: a FORCED attempt (the user tapping Retry)
// proceeds past gate 1, and past gate 3 it falls through to a fresh login. The offline
// bail (gate 2) stays for everyone — reconnecting is the only real fix there. Automatic
// retries (focus/online listeners, force=false) keep their original conservative
// behavior. Extracted so it is node-testable (same pattern as couch-login-online-gate.ts);
// auth.service.ts holds the state + side effects.

export type RetryCouchLoginAction =
  // nothing pending and not forced — skip (also the automatic-path no-user case)
  | 'skip-not-needed'
  // offline — a login retry can't reach the server; skip (even when forced)
  | 'skip-offline'
  // forced retry with no emitted user yet — do a fresh attemptCouchLoginAsync
  | 'fresh-login'
  // normal path — retry the couch login
  | 'retry';

export interface RetryCouchLoginGateInput {
  /** Is a deferred couch login pending (the flag armed by the offline-fallback path)? */
  couchLoginRetryNeeded: boolean;
  /** navigator.onLine at the moment of the attempt. */
  online: boolean;
  /** Has a user been emitted to the app yet (this.user != null)? */
  hasUser: boolean;
  /** true when the user tapped the splash Retry button (vs an automatic focus/online signal). */
  force: boolean;
}

export function resolveRetryCouchLoginGate(input: RetryCouchLoginGateInput): RetryCouchLoginAction {
  // pre-deferral hang: a forced (Retry-tap) attempt proceeds even when the retry-needed
  // flag was never armed; an automatic signal still skips when nothing is pending.
  if (!input.couchLoginRetryNeeded && !input.force) return 'skip-not-needed';
  // an offline retry is a no-op — nothing to reach. keep this bail even when forced;
  // reconnecting is the only real fix (the UI says so).
  if (!input.online) return 'skip-offline';
  // pre-login hang: the user hasn't been emitted yet. a normal retry can't re-emit, but a
  // forced attempt falls through to a fresh couch login which can (re)establish the session.
  if (!input.hasUser) return input.force ? 'fresh-login' : 'skip-not-needed';
  return 'retry';
}
